<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.loadingMessage"
    button-location="top"
    @submit="getAccounts()"
  >
    <template #title>
      {{ title }}
    </template>

    <template #top-form>
      <div
        v-if="showActions"
        class="row"
      >
        <p-button
          type="purple"
          class="ml-20 mobile-ml-10"
          round
          @click="createAccount"
        >
          Create New Account
        </p-button>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Email</label>
        <div class="col-sm-6">
          <sanitized-input
            id="icEmail"
            v-model="searchConditions.email"
            type="email"
            placeholder="Please input IC email"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Service</label>
        <div class="col-6">
          <dropdown-list
            v-model="searchConditions.service"
            :options="gourmetServiceList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Status</label>
        <div class="col-6">
          <dropdown-list
            v-model="searchConditions.isActive"
            :options="statusList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Closed</label>
        <div class="col-6">
          <dropdown-list
            v-model="closedFilter"
            :options="closedFilterList"
          />
        </div>
      </div>

      <div
        v-if="!defaultType"
        class="row align-items-center"
      >
        <label class="col-sm-3 control-label">Type</label>
        <div class="col-6">
          <dropdown-list
            v-model="searchConditions.type"
            :options="serviceAccountTypeList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Order By</label>
        <div class="col-sm-3 col-6">
          <dropdown-list
            v-model="searchConditions.orderBy"
            :options="orderByList"
          />
        </div>
        <div class="col-sm-3 col-6">
          <dropdown-list
            v-model="searchConditions.sortType"
            :options="sortTypeList"
          />
        </div>
      </div>

      <div class="form-submit-button-group pb-10 mobile-pb-10">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="status.isLoading"
        >
          Load Accounts
        </p-button>

        <p-button
          type="info"
          round
          outline
          :disabled="status.isLoading"
          @click="downloadAccounts"
        >
          Download
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="getAccounts"
        />
      </div>
      <message-html :result-html="status.resultHTML" />
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :highlight-list="highlightList"
        :data="serviceAccounts"
        :table-columns="tableColumns"
      >
        <template
          v-if="showActions"
          #right-fixed-columns
        >
          <el-table-column
            fixed="right"
            label="Active"
            width="50"
          >
            <template #default="scope">
              <el-checkbox
                v-model="scope.row.isActive"
                @change="onChangeActive($event, scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="DB"
            width="50"
          >
            <template #default="scope">
              <el-checkbox
                v-model="scope.row.isAutomatedAccount"
                @change="onChangeAutomatedAccount($event, scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="Manual"
            width="55"
          >
            <template #default="scope">
              <el-checkbox
                v-model="scope.row.isManualAccount"
                @change="onChangeManualAccount($event, scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="Edit"
            width="50"
          >
            <template #default="scope">
              <div
                class="info-icon"
                :class="{ 'text-red': !!scope.row.notes }"
                @click="editAccount(scope.row)"
              >
                <i class="fa fa-edit" />
              </div>
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="Credits"
            width="60"
          >
            <template #default="scope">
              <div
                class="info-icon"
                @click="openAddGift(scope.row)"
              >
                🎁
              </div>
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="Close"
            width="60"
          >
            <template #default="scope">
              <el-tooltip
                :content="scope.row.isClosed ? 'Reopen account' : 'Mark account as closed'"
                placement="top"
              >
                <div
                  class="info-icon"
                  @click="toggleClosed(scope.row)"
                >
                  <i :class="scope.row.isClosed ? 'fa fa-undo' : 'fa fa-times-circle'" />
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>

    <add-service-account-gift-credits-dialog
      :service-account="selectedAccount"
      :is-show-dialog="status.isOpenAddGiftDialog"
      @cancel="status.isOpenAddGiftDialog = false"
      @submit="addGift"
    />
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive } from 'vue';
import { useRouter } from 'vue-router';

import util from '@/Utils/util';
import { useGlobal } from '@/use/global';
import { useSwalDialog } from '@/use/swalDialog';
import { TableColumnType, HighlightTableItemType } from '@/models/table';
import { ListType } from '@/models/app';
import { gourmetServiceList } from '@/constants/deliveryOrder';
import { closedFilterList, closedFilterToBoolean, serviceAccountTypeList } from '@/constants/serviceAccount';
import { ServiceAccount } from '@/models/serviceAccount';
import { addServiceAccountGiftCredits } from '@/services/gourmet/addServiceAccountGiftCredits';
import { serviceAccountService } from '@/services/serviceAccount';

import AddServiceAccountGiftCreditsDialog from '@/components/Main/Dialogs/AddServiceAccountGiftCredits.vue';

const pageLimit = 30;
const tableColumns: TableColumnType[] = [
  { field: 'index', label: 'No', width: 60, isDisableSort: true, isCenterAlign: true },
  { field: 'assignedOperator', label: 'Assigned Operator', width: 220 },
  { field: 'user', label: 'User', width: 265 },
  { field: 'service', label: 'Service', width: 110 },
  { field: 'email', label: 'Email', width: 300 },
  { field: 'password', label: 'Password', width: 150 },
  { field: 'phoneNumber', label: 'Phone Number', width: 150 },
  { field: 'closedLabel', label: 'Closed', width: 100 },
  { field: 'cookieExpiry', label: 'Cookie Expiry', width: 200, isDateField: true },
];
const orderByList: ListType[] = [
  { id: 1, key: 'email', label: 'Email' },
  { id: 2, key: 'updatedAt', label: 'Last Used Date' },
];
const sortTypeList: ListType[] = [
  { id: 1, key: 'ASC', label: 'Ascending' },
  { id: 2, key: 'DESC', label: 'Descending' },
];
const statusList: ListType[] = [
  { id: 1, key: '', label: 'All' },
  { id: 1, key: 'active', label: 'Active' },
  { id: 2, key: 'disabled', label: 'Disabled' },
];

const props = defineProps({
  showActions: {
    type: Boolean,
    default: false,
  },
  pageId: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  defaultType: {
    type: String,
    default: '',
  },
});

const highlightList: Ref<HighlightTableItemType[]> = ref([]);

const router = useRouter();

const { setServiceAccountId } = useGlobal();
const { showErrorPopupDialog, showSuccessPopupDialog, showConfirmPopupDialog } = useSwalDialog();

const selectedAccount: Ref<ServiceAccount | null> = ref(null);
const serviceAccounts: Ref<ServiceAccount[]> = ref([]);
const closedFilter = ref(closedFilterList[0].key);
const searchConditions: Ref<any> = ref({
  email: '',
  isActive: '',
  type: serviceAccountTypeList[0].key,
  service: gourmetServiceList[0].key,
  orderBy: orderByList[1].key,
  sortType: sortTypeList[1].key,
});
const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});
const status = reactive({
  isLoading: false,
  isOpenAddGiftDialog: false,
  loadingMessage: '',
  resultHTML: '',
});

const getAccountTypeValues = (type: string) => {
  if (type === 'db') {
    return {
      isAutomatedAccount: true,
    };
  }

  if (type === 'manual') {
    return {
      isManualAccount: true,
    };
  }

  return {};
};

const buildQueryParams = () => ({
  ...searchConditions.value,
  isActive: searchConditions.value.isActive === 'active' ? true : searchConditions.value.isActive === 'disabled' ? false : null,
  isClosed: closedFilterToBoolean(closedFilter.value),
  ...getAccountTypeValues(props.defaultType || searchConditions.value.type),
});

const getAccounts = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * pageLimit;
  paginationInfo.currentPage = currentPage;

  status.resultHTML = '';
  status.isLoading = true;
  status.loadingMessage = 'Loading Service Accounts...';

  try {
    const response = await serviceAccountService.fetchServiceAccounts({
      ...buildQueryParams(),
      pagination: {
        limit: pageLimit,
        offset,
      },
    });

    highlightList.value = [];
    serviceAccounts.value = response.rows.map((item: ServiceAccount, index: number) => {
      highlightList.value.push({ index, class: item.isActive ? 'text-green' : 'text-red', type: 'row' });
      return {
        ...item,
        service: util.getLabelByField(gourmetServiceList, item.service),
        assignedOperator: util.getFullName(item.operator?.firstName ?? '', item.operator?.lastName ?? ''),
        closedLabel: item.isClosed ? 'Closed' : 'Open',
        index: offset + index + 1,
        orderIndex: index,
      };
    });

    paginationInfo.totalPages = Math.ceil(response.count / pageLimit) || 1;
  } catch (error) {
    serviceAccounts.value = [];
    highlightList.value = [];
    paginationInfo.currentPage = 1;
    paginationInfo.totalPages = 1;

    status.resultHTML = util.getGraphqlError(error);
  } finally {
    status.isLoading = false;
  }
};

const downloadAccounts = async () => {
  status.isLoading = true;
  status.loadingMessage = 'Preparing Service Accounts to Download...';

  try {
    const csv = await serviceAccountService.downloadServiceAccounts(buildQueryParams());

    util.downloadJSONToCSV(csv, 'ServiceAccounts.csv');

    showSuccessPopupDialog({
      text: 'Service accounts have been downloaded successfully.',
    });
  } catch (error) {
    showErrorPopupDialog({
      text: util.getGraphqlError(error, false),
    });
  } finally {
    status.isLoading = false;
  }
};

const toggleClosed = async (serviceAccount: ServiceAccount) => {
  const nextClosed = !serviceAccount.isClosed;

  const isConfirmed = await showConfirmPopupDialog({
    title: nextClosed ? 'Close Account?' : 'Reopen Account?',
    text: nextClosed
      ? `Are you sure you want to mark ${serviceAccount.email} as closed? It will no longer be available for assignment.`
      : `Are you sure you want to reopen ${serviceAccount.email}?`,
    confirmButtonText: 'Confirm',
  });

  if (!isConfirmed) {
    return;
  }

  status.isLoading = true;
  status.loadingMessage = `${nextClosed ? 'Closing' : 'Reopening'} ${serviceAccount.email} account...`;

  try {
    await serviceAccountService.updateServiceAccount({
      id: serviceAccount.id,
      data: {
        ...getUpdateData(serviceAccount),
        isClosed: nextClosed,
      },
    });

    showSuccessPopupDialog({
      text: `${serviceAccount.email} has been ${nextClosed ? 'closed' : 'reopened'} successfully.`,
    });

    await getAccounts(paginationInfo.currentPage);
  } catch (error) {
    showErrorPopupDialog({
      text: util.getGraphqlError(error, false),
    });
  } finally {
    status.isLoading = false;
  }
};

const createAccount = () => {
  setServiceAccountId('');
  router.push({
    name: 'CreateServiceAccount',
  });
};

const editAccount = (row: ServiceAccount) => {
  setServiceAccountId(row.id);
  router.push({
    name: 'UpdateServiceAccount',
  });
};

const openAddGift = (row: ServiceAccount) => {
  selectedAccount.value = row;
  status.isOpenAddGiftDialog = true;
};

const addGift = async (data: { code: string; email: string }) => {
  status.isOpenAddGiftDialog = false;
  status.isLoading = true;
  status.loadingMessage = `Adding gift credits to ${data.email} account...`;

  try {
    const reponse = await addServiceAccountGiftCredits(data);
    const amount = (reponse / 100).toFixed(2);
    showSuccessPopupDialog({
      text: `Gift credits have been added to ${data.email}’s account successfully. $${amount} credits are now available.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const getUpdateData = (serviceAccount: ServiceAccount) => {
  return {
    user: serviceAccount.user,
    service: gourmetServiceList.find((item) => item.label === serviceAccount.service)?.key || '',
    email: serviceAccount.email,
    password: serviceAccount.password,
    phoneNumber: serviceAccount.phoneNumber,
    isAutomatedAccount: serviceAccount.isAutomatedAccount,
    isManualAccount: serviceAccount.isManualAccount,
    isActive: serviceAccount.isActive,
    isClosed: serviceAccount.isClosed,
  };
};

const onChangeActive = async (isActive: boolean, serviceAccount: ServiceAccount) => {
  const confirmText = `Are you sure ${isActive ? 'activate' : 'deactivate'} ${serviceAccount.email} account?`;
  const confirmTitle = `${isActive ? 'Activate' : 'Deactivate'} Account?`;

  const isConfirmed = await showConfirmPopupDialog({
    text: confirmText,
    title: confirmTitle,
    confirmButtonText: 'Confirm',
  });

  if (!isConfirmed) {
    serviceAccount.isActive = !isActive;
  }

  status.isLoading = true;
  status.loadingMessage = `${isActive ? 'Activating' : 'Deactivating'} ${serviceAccount.email} account...`;

  try {
    await serviceAccountService.updateServiceAccount({
      id: serviceAccount.id,
      data: {
        ...getUpdateData(serviceAccount),
        isActive,
      },
    });

    showSuccessPopupDialog({
      text: `${serviceAccount.email} account has been ${isActive ? 'activated' : 'deactivated'} successfully.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    serviceAccount.isActive = !isActive;
  } finally {
    status.isLoading = false;
  }
};

const onChangeAutomatedAccount = async (isAutomatedAccount: boolean, serviceAccount: ServiceAccount) => {
  const confirmText = `Are you sure ${isAutomatedAccount ? 'enable' : 'disable'} DB ordering for ${serviceAccount.email} account?`;
  const confirmTitle = `${isAutomatedAccount ? 'Enable' : 'Disable'} DB Ordering?`;

  const isConfirmed = await showConfirmPopupDialog({
    text: confirmText,
    title: confirmTitle,
    confirmButtonText: 'Confirm',
  });

  const isManualAccount = serviceAccount.isManualAccount;

  if (!isConfirmed) {
    serviceAccount.isAutomatedAccount = !isAutomatedAccount;
  }

  if (isAutomatedAccount) {
    serviceAccount.isManualAccount = false;
  }

  status.isLoading = true;
  status.loadingMessage = `${isAutomatedAccount ? 'Enabling' : 'Disabling'} DB ordering ${serviceAccount.email} account...`;

  try {
    await serviceAccountService.updateServiceAccount({
      id: serviceAccount.id,
      data: {
        ...getUpdateData(serviceAccount),
        isAutomatedAccount,
      },
    });

    showSuccessPopupDialog({
      text: `DB ordering ${serviceAccount.email} account has been ${isAutomatedAccount ? 'enabled' : 'disabled'} successfully.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    serviceAccount.isAutomatedAccount = !isAutomatedAccount;
    serviceAccount.isManualAccount = isManualAccount;
  } finally {
    status.isLoading = false;
  }
};

const onChangeManualAccount = async (isManualAccount: boolean, serviceAccount: ServiceAccount) => {
  const confirmText = `Are you sure ${isManualAccount ? 'enable' : 'disable'} manual account(${serviceAccount.email})?`;
  const confirmTitle = `${isManualAccount ? 'Enable' : 'Disable'} Manual Account?`;

  const isConfirmed = await showConfirmPopupDialog({
    text: confirmText,
    title: confirmTitle,
    confirmButtonText: 'Confirm',
  });
  const isAutomatedAccount = serviceAccount.isAutomatedAccount;

  if (!isConfirmed) {
    serviceAccount.isManualAccount = !isManualAccount;
  }

  if (isManualAccount) {
    serviceAccount.isAutomatedAccount = false;
  }

  status.isLoading = true;
  status.loadingMessage = `${isManualAccount ? 'Enabling' : 'Disabling'} manual account(${serviceAccount.email})...`;

  try {
    await serviceAccountService.updateServiceAccount({
      id: serviceAccount.id,
      data: {
        ...getUpdateData(serviceAccount),
        isManualAccount,
      },
    });

    showSuccessPopupDialog({
      text: `The manual account (${serviceAccount.email}) has been ${isManualAccount ? 'enabled' : 'disabled'} successfully.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    serviceAccount.isManualAccount = !isManualAccount;
    serviceAccount.isAutomatedAccount = isAutomatedAccount;
  } finally {
    status.isLoading = false;
  }
};

const resetForm = () => {
  status.resultHTML = '';
  serviceAccounts.value = [];

  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;

  closedFilter.value = closedFilterList[0].key;
  searchConditions.value = {
    email: '',
    isActive: '',
    type: serviceAccountTypeList[0].key,
    service: gourmetServiceList[0].key,
    orderBy: orderByList[1].key,
    sortType: sortTypeList[1].key,
  };
};
</script>
